import { useMemo, useState } from "react";
import type { Catalog, ValueDef } from "../../api/types";
import { Modal } from "../../components/ui";
import { emptyValue } from "./model";

type Tab = "parameters" | "payload" | "inputs" | "flight" | "derived";

export function ValuePicker({
  catalog,
  triggers,
  existing,
  onAdd,
  onClose,
}: {
  catalog: Catalog;
  triggers: string[];
  existing: Set<string>;
  onAdd: (values: ValueDef[]) => void;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<Tab>("parameters");
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState<Map<string, ValueDef>>(new Map());

  const payloadKeys = useMemo(() => {
    const out: { key: string; trigger: string; label: string }[] = [];
    for (const [trig, labels] of Object.entries(catalog.payload_keys)) {
      if (triggers.length && !triggers.includes(trig)) continue;
      for (const [label, keys] of Object.entries(labels)) for (const k of keys) out.push({ key: k, trigger: trig, label });
    }
    return out;
  }, [catalog, triggers]);

  const items: { key: string; label: string; sub?: string; source: ValueDef["source"]; units?: string | null }[] = useMemo(() => {
    const f = q.trim().toLowerCase();
    const match = (s: string) => !f || s.toLowerCase().includes(f);
    switch (tab) {
      case "parameters":
        return catalog.parameters
          .filter((p) => match(p.mnemonic) || match(p.name ?? ""))
          .map((p) => ({ key: p.mnemonic, label: p.mnemonic, sub: `${p.name ?? ""} ${p.units ?? ""} @${p.rate} Hz`.trim(), source: "parameter" as const, units: p.units }));
      case "derived":
        return catalog.derived.filter(match).map((d) => ({ key: d, label: d, sub: "derived parameter", source: "parameter" as const }));
      case "payload":
        return payloadKeys.filter((p) => match(p.key)).map((p) => ({ key: p.key, label: p.key, sub: `${p.trigger} · ${p.label}`, source: "payload" as const }));
      case "inputs":
        return catalog.inputs.filter(match).map((k) => ({ key: k, label: k, source: "input" as const }));
      case "flight":
        return catalog.flight_fields.filter(match).map((k) => ({ key: k, label: k, source: "flight" as const }));
    }
  }, [tab, q, catalog, payloadKeys]);

  const toggle = (it: (typeof items)[number]) => {
    const next = new Map(picked);
    if (next.has(it.key)) next.delete(it.key);
    else next.set(it.key, { ...emptyValue(it.key, it.source), units: it.units ?? null });
    setPicked(next);
  };

  return (
    <Modal
      title="Add values"
      onClose={onClose}
      width={640}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" disabled={!picked.size} onClick={() => onAdd([...picked.values()])}>
            Add {picked.size || ""}
          </button>
        </>
      }
    >
      <div className="tabs" style={{ margin: "-14px -16px 6px" }}>
        {(
          [
            ["parameters", `Frame parameters (${catalog.parameters.length})`],
            ["derived", `Derived (${catalog.derived.length})`],
            ["payload", `Snapshot payload (${payloadKeys.length})`],
            ["inputs", `Inputs (${catalog.inputs.length})`],
            ["flight", "Flight"],
          ] as [Tab, string][]
        ).map(([t, label]) => (
          <button key={t} className={tab === t ? "on" : ""} onClick={() => setTab(t)}>
            {label}
          </button>
        ))}
      </div>
      {tab === "payload" ? (
        <p className="dim small" style={{ margin: 0 }}>
          Keys emitted by <code>ctx.snapshot(..., values=&#123;...&#125;)</code>, discovered from the last finished run. Type a key below
          to add one that has not been observed yet.
        </p>
      ) : null}
      <div className="row">
        <input className="inp sm" style={{ flex: 1 }} autoFocus placeholder="filter…" value={q} onChange={(e) => setQ(e.target.value)} />
        {tab === "payload" || tab === "inputs" ? (
          <button
            className="btn sm"
            disabled={!q.trim()}
            onClick={() => {
              const key = q.trim();
              const next = new Map(picked);
              next.set(key, emptyValue(key, tab === "payload" ? "payload" : "input"));
              setPicked(next);
            }}
          >
            Add "{q.trim() || "…"}" as custom key
          </button>
        ) : null}
      </div>
      <div className="browse-list" style={{ maxHeight: 360 }}>
        {items.map((it) => (
          <div
            key={it.key}
            className={`list-item${picked.has(it.key) ? " sel" : ""}`}
            onClick={() => toggle(it)}
            style={existing.has(it.key) ? { opacity: 0.5 } : undefined}
          >
            <input type="checkbox" readOnly checked={picked.has(it.key)} />
            <span className="name mono">{it.label}</span>
            {it.sub ? <span className="dim small">{it.sub}</span> : null}
            {existing.has(it.key) ? <span className="badge">already added</span> : null}
          </div>
        ))}
        {!items.length ? <div className="empty small">nothing here</div> : null}
      </div>
    </Modal>
  );
}
